<template>
  <div class="ui-docview">
    <div v-if="block.props.prompt" class="ui-docview__prompt">{{ block.props.prompt }}</div>
    <div class="ui-docview__header">
      <div>
        <div class="ui-docview__name">{{ doc.title || doc.name }}</div>
        <div class="ui-docview__meta">
          {{ doc.reference }}<template v-if="doc.doc_type"> · {{ doc.doc_type }}</template><template v-if="doc.status"> · {{ doc.status }}</template>
        </div>
      </div>
      <div class="ui-docview__actions">
        <!-- Standard document-summary popover — only offered inside the
             RoleMapper app (the host resolves page-handler vs its own
             fallback popup). -->
        <button
          v-if="canShowSummary"
          type="button"
          class="ui-docview__open"
          :title="`Preview ${doc.reference}`"
          @click="openSummary($event)"
        >Summary</button>
        <button
          v-if="canOpenInApp"
          type="button"
          class="ui-docview__open"
          :title="`Open ${doc.reference}`"
          @click="openDoc"
        >Open</button>
        <a
          v-else-if="doc.portal_url"
          :href="doc.portal_url"
          target="_blank"
          rel="noopener"
          class="ui-docview__open"
          title="Open in RoleMapper"
        >Open in RoleMapper ↗</a>
      </div>
    </div>

    <!-- Inline content — suppressed on compact cards (post-creation
         view: the document opens itself; Open/Summary cover the rest). -->
    <div v-if="!block.props.compact" class="ui-docview__parts">
      <div v-for="(part, pi) in doc.parts" :key="pi" class="ui-docview__part">
        <div class="ui-docview__part-name">{{ part.name }}</div>
        <div v-if="part.text" class="ui-docview__part-text">{{ part.text }}</div>
        <ul v-if="part.items && part.items.length" class="ui-docview__items">
          <li v-for="(item, ii) in part.items" :key="ii">{{ item }}</li>
        </ul>
      </div>
      <div v-if="doc.truncated" class="ui-docview__truncated">
        Showing a summary — open in RoleMapper for the full document.
      </div>
    </div>

    <!--
      Confirm only exists when the step has somewhere to go: a doc_view
      emitted as the workflow's END POINT (no confirmed link) arrives
      with workflow.status 'completed' — pure display, no button and no
      "no longer active" note either.
    -->
    <button v-if="interactive && awaitingInput" type="button" class="ui-docview__confirm" @click="confirm">
      {{ block.props.confirm_label || 'Continue' }}
    </button>
    <div v-else-if="!interactive && awaitingInput" class="ui-docview__inert">This step is no longer active.</div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

/**
 * `doc_view` ui_block — read-only display of the document selected in a
 * preceding step (content engine-resolved, permission-checked).
 * Confirming emits `action` with `{ key: 'confirmed', label, value }`
 * (value = the reference, for downstream convenience).
 *
 * The header "Open" action uses the standard in-app document view when
 * the host page registered `document.open` (`openAvailable`); otherwise
 * it falls back to the viewdoc deep link in a new tab.
 */
export default defineComponent({
  name: 'UiBlockDocView',
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    /** True when the host page registered the document.open action. */
    openAvailable: { type: Boolean, default: false },
    /** True when a document-summary popover is available (in-app only —
     * the MCP surface never sets it). */
    summaryAvailable: { type: Boolean, default: false },
  },
  emits: ['action', 'open-document', 'open-summary'],
  setup(props, { emit }) {
    const doc = computed(() => props.block.props.document);

    // False when the engine emitted this doc_view as the workflow's end
    // point (workflow.status 'completed') — there is no outcome to fire.
    const awaitingInput = computed(
      () => props.block.workflow?.status === 'awaiting_input',
    );

    // In-app open needs a resolvable doc_id (the spec coerces with
    // Number(), so a missing id arrives as NaN — hence isFinite).
    const canOpenInApp = computed(
      () => props.openAvailable && Number.isFinite(doc.value.doc_id),
    );

    const canShowSummary = computed(
      () => props.summaryAvailable && Number.isFinite(doc.value.doc_id),
    );

    function openDoc() {
      emit('open-document', {
        reference: doc.value.reference,
        doc_id: doc.value.doc_id,
        doc_name: doc.value.title || doc.value.name,
        // Present only on a post-creation card — the host uses it to open
        // the Atlas panel on the document with a note about what it made.
        created_note: props.block.props.created_note ?? null,
      });
    }

    function openSummary(event) {
      emit('open-summary', event, {
        reference: doc.value.reference,
        doc_id: doc.value.doc_id,
        doc_name: doc.value.title || doc.value.name,
      });
    }

    function confirm() {
      if (!props.interactive) return;
      emit('action', {
        key: 'confirmed',
        label: `Continue with ${doc.value.name} (${doc.value.reference})`,
        value: doc.value.reference,
      });
    }

    return { doc, confirm, canOpenInApp, canShowSummary, openDoc, openSummary, awaitingInput };
  },
});
</script>

<style scoped>
.ui-docview {
  margin-top: 10px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgb(199 210 254);
  background: linear-gradient(to bottom, rgba(238, 242, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%);
}
.ui-docview__prompt {
  font-size: 13px;
  font-weight: 600;
  color: rgb(49 46 129);
  margin-bottom: 10px;
}
.ui-docview__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgb(224 231 255);
  margin-bottom: 8px;
}
.ui-docview__name {
  font-size: 14px;
  font-weight: 700;
  color: rgb(31 41 55);
}
.ui-docview__meta {
  font-size: 11px;
  color: rgb(107 114 128);
}
.ui-docview__actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
/* Shared between the in-app <button> and the deep-link <a> fallback. */
.ui-docview__open {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: rgb(79 70 229);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}
.ui-docview__open:hover {
  text-decoration: underline;
}
.ui-docview__parts {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}
.ui-docview__part {
  margin-bottom: 8px;
}
.ui-docview__part-name {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgb(75 85 99);
  margin-bottom: 2px;
}
.ui-docview__part-text {
  font-size: 12.5px;
  color: rgb(31 41 55);
  white-space: pre-wrap;
}
.ui-docview__items {
  margin: 2px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: rgb(31 41 55);
}
.ui-docview__items li {
  margin-bottom: 2px;
}
.ui-docview__truncated {
  font-size: 11px;
  font-style: italic;
  color: rgb(107 114 128);
}
.ui-docview__confirm {
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  padding: 7px 16px;
  border: 0;
  border-radius: 8px;
  background: rgb(79 70 229);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;
}
.ui-docview__confirm:hover {
  background: rgb(67 56 202);
}
.ui-docview__inert {
  margin-top: 8px;
  font-size: 12px;
  color: rgb(107 114 128);
}
</style>
